import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import './Dashboard.css';
import { motion, AnimatePresence } from 'framer-motion';
import { FaProjectDiagram, FaGlobe, FaCalendarAlt, FaUserEdit, FaEdit, FaTimes, FaSave, FaPalette, FaIcons, FaPlus, FaTrash, FaMousePointer, FaClock, FaRoute, FaFilter } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { safeFetch } from '../utils/apiUtils';

const Dashboard = () => {
    const [stats, setStats] = useState([
        { id: 1, label: "Years Experience", value: "10+", color: "#FF9F1C", subtitle: "+2 this year", icon: 'calendar_today' },
        { id: 2, label: "Projects Delivered", value: "250+", color: "#3b82f6", subtitle: "+12 this month", icon: 'account_tree' },
        { id: 3, label: "Global Clients", value: "80+", color: "#10b981", subtitle: "+5 countries", icon: 'public' }
    ]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCreateMode, setIsCreateMode] = useState(false);
    const [editingStat, setEditingStat] = useState(null);
    const [trafficData, setTrafficData] = useState([]);
    const [trafficFilter, setTrafficFilter] = useState('real-time'); // real-time, 24h, 7d
    const [activeUsers, setActiveUsers] = useState(0);
    const [trafficSummary, setTrafficSummary] = useState({ mostVisited: '/', topSource: 'Direct' });
    const [isTrafficLoading, setIsTrafficLoading] = useState(false);
    const [formData, setFormData] = useState({
        label: '',
        value: '',
        subtitle: '',
        icon: 'calendar_today',
        color: '#3b82f6'
    });

    useEffect(() => {
        fetchDashboardStats();
        fetchTrafficData();

        // Polling for real-time feel if in real-time mode
        const interval = setInterval(() => {
            if (trafficFilter === 'real-time') {
                fetchTrafficData();
            }
        }, 30000); // every 30 seconds

        return () => clearInterval(interval);
    }, [trafficFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchTrafficData = async () => {
        setIsTrafficLoading(true);
        try {
            const { ok, data } = await safeFetch(`https://aayushrealproperties.in/nimaibackend/public/api/traffic-stats?filter=${trafficFilter}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
                    'Accept': 'application/json'
                }
            });

            if (ok) {
                const result = data.data || data;
                // Match actual API keys based on the response provided
                setTrafficData(result.hits || []);
                setActiveUsers(result.active_users || 0);
                setTrafficSummary({
                    mostVisited: result.metrics?.most_visited?.path || '/',
                    topSource: result.metrics?.top_source?.referrer || 'Direct'
                });
            } else {
                console.error('Traffic stats fetch failed:', data);
            }
        } catch (error) {
            console.error('Error fetching traffic data:', error);
        } finally {
            setIsTrafficLoading(false);
        }
    };

    const fetchDashboardStats = async () => {
        setIsLoading(true);
        try {
            const { ok, data: result } = await safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/experience-stats', {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                }
            });
            if (ok) {
                const data = result.data || result;
                const fetchedStats = Array.isArray(data) ? data : Object.values(data);
                setStats(fetchedStats);
            }
        } catch (error) {
            console.error('Error fetching dashboard stats:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const getIconComponent = (iconName) => {
        switch (iconName?.toLowerCase()) {
            case 'calendar_today': return <FaCalendarAlt />;
            case 'account_tree': return <FaProjectDiagram />;
            case 'public': return <FaGlobe />;
            default: return <FaProjectDiagram />;
        }
    };

    const handleEditClick = (stat) => {
        setEditingStat(stat);
        setIsCreateMode(false);
        setFormData({
            label: stat.label,
            value: stat.value,
            subtitle: stat.subtitle || stat.trend || '',
            icon: stat.icon || 'calendar_today',
            color: stat.color || '#3b82f6'
        });
        setIsModalOpen(true);
    };

    const handleAddClick = () => {
        setIsCreateMode(true);
        setEditingStat(null);
        setFormData({
            label: '',
            value: '',
            subtitle: '',
            icon: 'calendar_today',
            color: '#3b82f6'
        });
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this statistic?')) return;

        try {
            const { ok } = await safeFetch(`https://aayushrealproperties.in/nimaibackend/public/api/experience-stats/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
                    'Accept': 'application/json'
                }
            });

            if (ok) {
                toast.success('Statistic deleted');
                fetchDashboardStats();
            } else {
                setStats(stats.filter(s => s.id !== id));
                toast.success('Deleted locally');
            }
        } catch (error) {
            console.error('Error deleting stat:', error);
            setStats(stats.filter(s => s.id !== id));
            toast.success('Deleted locally');
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        const url = isCreateMode
            ? 'https://aayushrealproperties.in/nimaibackend/public/api/experience-stats'
            : `https://aayushrealproperties.in/nimaibackend/public/api/experience-stats/${editingStat.id}`;
        const method = isCreateMode ? 'POST' : 'PUT';

        try {
            const { ok } = await safeFetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
                    'Accept': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            if (ok) {
                toast.success(`Statistic ${isCreateMode ? 'created' : 'updated'} successfully`);
                fetchDashboardStats();
                setIsModalOpen(false);
            } else {
                if (isCreateMode) {
                    setStats([...stats, { ...formData, id: Date.now() }]);
                } else {
                    setStats(stats.map(s => s.id === editingStat.id ? { ...s, ...formData } : s));
                }
                toast.success('Saved locally (Sync pending)');
                setIsModalOpen(false);
            }
        } catch (error) {
            console.error('Error saving stat:', error);
            if (isCreateMode) {
                setStats([...stats, { ...formData, id: Date.now() }]);
            } else {
                setStats(stats.map(s => s.id === editingStat.id ? { ...s, ...formData } : s));
            }
            toast.success('Network issue: Saved locally');
            setIsModalOpen(false);
        }
    };

    return (
        <div className="admin-layout">
            <Sidebar />
            <main className="admin-content">
                <header className="content-header">
                    <div>
                        <h1>Dashboard <span>Overview</span></h1>
                        <p>Welcome back, Administrator. Here's a summary of your reach.</p>
                    </div>
                    <div className="admin-actions">
                        <button className="btn-add-stat" onClick={handleAddClick}>
                            <FaPlus /> Add New Stat
                        </button>
                        <div className="admin-profile-quick">
                            <div className="profile-info">
                                <span className="name">Rahul Machhi</span>
                                <span className="role">Platform Admin</span>
                            </div>
                            <div className="profile-avatar">
                                <FaUserEdit />
                            </div>
                        </div>
                    </div>
                </header>

                <div className="stats-grid">
                    {isLoading ? (
                        <div className="dashboard-loader-container" style={{ gridColumn: '1/-1', textAlign: 'center', padding: '50px' }}>
                            <div className="loader"></div>
                        </div>
                    ) : (
                        stats.map((stat, index) => (
                            <motion.div
                                key={stat.id}
                                className="stat-card"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <div className="stat-icon" style={{ backgroundColor: `${stat.color || '#3b82f6'}15`, color: stat.color || '#3b82f6' }}>
                                    {getIconComponent(stat.icon)}
                                </div>
                                <div className="stat-info">
                                    <h3>{stat.label}</h3>
                                    <div className="stat-value">{stat.value}</div>
                                    <span className="stat-trend">{stat.subtitle || stat.trend}</span>
                                </div>
                                <div className="stat-card-actions">
                                    <button className="edit-stat-btn" onClick={() => handleEditClick(stat)} title="Edit">
                                        <FaEdit />
                                    </button>
                                    <button className="delete-stat-btn" onClick={() => handleDelete(stat.id)} title="Delete">
                                        <FaTrash />
                                    </button>
                                </div>
                            </motion.div>
                        ))
                    )}
                </div>

                <div className="traffic-section">
                    <div className="section-header">
                        <div className="header-left">
                            <h2>Real-time Traffic <span>Monitor</span></h2>
                            <p>Tracking live user interactions across non-admin pages.</p>
                        </div>
                        <div className="header-right">
                            <div className="traffic-filters">
                                <button
                                    className={trafficFilter === 'real-time' ? 'active' : ''}
                                    onClick={() => setTrafficFilter('real-time')}
                                >
                                    Real-time
                                </button>
                                <button
                                    className={trafficFilter === '24h' ? 'active' : ''}
                                    onClick={() => setTrafficFilter('24h')}
                                >
                                    Last 24h
                                </button>
                                <button
                                    className={trafficFilter === '7d' ? 'active' : ''}
                                    onClick={() => setTrafficFilter('7d')}
                                >
                                    Last 7d
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="traffic-grid">
                        <div className="traffic-main-card">
                            <div className="active-users-badge">
                                <span className="pulse-dot"></span>
                                {activeUsers} Active Users Now
                            </div>

                            <div className="recent-visits-table">
                                <div className="table-header">
                                    <span>Page Path</span>
                                    <span>Time</span>
                                    <span>Device</span>
                                    <span>Duration</span>
                                </div>
                                <div className="table-body">
                                    {isTrafficLoading ? (
                                        <div className="table-loader">Loading traffic data...</div>
                                    ) : trafficData.length > 0 ? (
                                        trafficData.map((visit, idx) => (
                                            <motion.div
                                                key={visit.id || idx}
                                                className="table-row"
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: idx * 0.05 }}
                                            >
                                                <span className="path"><FaRoute /> {visit.path}</span>
                                                <span className="time"><FaClock /> {new Date(visit.created_at || visit.timestamp).toLocaleTimeString()}</span>
                                                <span className="device">{visit.device_type || visit.device || 'Desktop'}</span>
                                                <span className="duration">{visit.duration || '< 1m'}</span>
                                            </motion.div>
                                        ))
                                    ) : (
                                        <div className="empty-state">No traffic data found for this period.</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="traffic-stats-sidebar">
                            <div className="mini-stat-card">
                                <div className="mini-icon"><FaMousePointer /></div>
                                <div className="mini-info">
                                    <h4>Most Visited</h4>
                                    <p>{trafficSummary.mostVisited}</p>
                                </div>
                            </div>
                            <div className="mini-stat-card">
                                <div className="mini-icon"><FaFilter /></div>
                                <div className="mini-info">
                                    <h4>Top Source</h4>
                                    <p>{trafficSummary.topSource}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Edit/Create Modal */}
                <AnimatePresence>
                    {isModalOpen && (
                        <motion.div
                            className="modal-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <motion.div
                                className="stat-edit-modal"
                                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                                animate={{ scale: 1, opacity: 1, y: 0 }}
                                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            >
                                <div className="modal-header">
                                    <h2>{isCreateMode ? 'Add New Statistic' : 'Edit Statistic'}</h2>
                                    <button className="close-btn" onClick={() => setIsModalOpen(false)}>
                                        <FaTimes />
                                    </button>
                                </div>

                                <form onSubmit={handleSave} className="admin-form">
                                    <div className="form-group">
                                        <label>Label</label>
                                        <input
                                            type="text"
                                            value={formData.label}
                                            onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                                            placeholder="e.g., Projects Delivered"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Value</label>
                                        <input
                                            type="text"
                                            value={formData.value}
                                            onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                                            placeholder="e.g., 250+"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Subtitle / Trend</label>
                                        <input
                                            type="text"
                                            value={formData.subtitle}
                                            onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                                            placeholder="e.g., +12 this month"
                                        />
                                    </div>
                                    <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                        <div className="form-group">
                                            <label><FaIcons /> Icon Key</label>
                                            <select
                                                value={formData.icon}
                                                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                                            >
                                                <option value="calendar_today">Calendar</option>
                                                <option value="account_tree">Projects</option>
                                                <option value="public">Global</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label><FaPalette /> Color</label>
                                            <input
                                                type="color"
                                                value={formData.color}
                                                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    <div className="modal-footer">
                                        <button type="button" className="btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
                                        <button type="submit" className="btn-primary">
                                            <FaSave /> {isCreateMode ? 'Create' : 'Save Changes'}
                                        </button>
                                    </div>
                                </form>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>
        </div>
    );
};

export default Dashboard;
